/* =========================================================
   FREE GIFT – Sense Theme (Cart Page + Drawer)
   ---------------------------------------------------------
   Panel HTML:
   <div class="promo-free-gift-panel hidden"
        data-gift-variant-id="GIFT_VARIANT_ID"
        data-eligible-collections="collection-handle">
     <button class="pfp__btn" type="button">Add for Free</button>
   </div>

   Notes:
   - Gift variant MUST be different from paid variant.
   - This script refreshes the cart PAGE by dynamically detecting
     the correct cart sections on your theme (no hardcoded names).
========================================================= */

(() => {
  /* ---------------- fetch helpers ---------------- */
  const fetchJSON = (url, opts = {}) =>
    fetch(url, opts).then((r) => {
      if (!r.ok) throw new Error(`HTTP ${r.status} @ ${url}`);
      return r.json();
    });

  const getCart = () => fetchJSON("/cart.js");

  const addVariant = (variantId, qty = 1, properties = {}) =>
    fetchJSON("/cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        id: Number(variantId),
        quantity: Number(qty),
        properties,
      }),
    });

  const changeLine = (line, quantity) =>
    fetchJSON("/cart/change.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ line, quantity }),
    });

  /* ---------------- utils ---------------- */
  const debounce = (fn, ms = 120) => {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn(...args), ms);
    };
  };

  const panelsSelector = ".promo-free-gift-panel, .free-gift-panel";
  const getPanels = () => Array.from(document.querySelectorAll(panelsSelector));

  function getConfig(panel) {
    const giftVariantId =
      panel.dataset.giftVariantId || panel.getAttribute("data-gift-variant-id");

    const handlesRaw =
      panel.dataset.eligibleCollections ||
      panel.getAttribute("data-eligible-collections") ||
      "";

    const handles = handlesRaw
      .split(",")
      .map((h) => h.trim())
      .filter(Boolean);

    const autoRemove =
      String(panel.dataset.autoRemove ?? panel.getAttribute("data-auto-remove") ?? "1") !== "0";

    return { giftVariantId, handles, autoRemove };
  }

  /* ---------------- eligibility cache ---------------- */
  let cachedEligibleIds = null;
  let cachedHandlesKey = null;

  async function getEligibleProductIds(handles) {
    const all = new Set();
    for (const raw of handles) {
      const handle = (raw || "").trim();
      if (!handle) continue;

      let page = 1;
      let more = true;
      while (more) {
        const data = await fetchJSON(
          `/collections/${handle}/products.json?limit=250&page=${page}`
        );
        (data.products || []).forEach((p) => all.add(p.id));
        more = (data.products || []).length === 250;
        page++;
      }
    }
    return all;
  }

  /* ---------------- cart checks ---------------- */
  const cartHasGift = (cart, giftVariantId) =>
    cart.items.some((i) => String(i.variant_id) === String(giftVariantId));

  const cartHasEligible = (cart, eligibleIds) =>
    cart.items.some((i) => eligibleIds.has(i.product_id));

  async function removeGiftIfPresent(cart, giftVariantId) {
    const idx = cart.items.findIndex(
      (i) => String(i.variant_id) === String(giftVariantId)
    );
    if (idx !== -1) await changeLine(idx + 1, 0);
  }

  /* =========================================================
     CART PAGE REFRESH (self-detecting)
     ---------------------------------------------------------
     Detect cart-related section wrappers on the CURRENT cart page,
     fetch those exact sections, and replace them.
  ========================================================= */

  function detectCartSectionNamesOnPage() {
    // Shopify OS2 sections wrap like: <div id="shopify-section-SECTIONNAME">
    const wrappers = Array.from(document.querySelectorAll('[id^="shopify-section-"]'));

    // Only on /cart — and only sections likely related to cart
    const names = wrappers
      .map((el) => el.id.replace(/^shopify-section-/, ""))
      .filter((name) => {
        const n = name.toLowerCase();
        // keep broad so it works across Sense variants
        return n.includes("cart") || n.includes("main") || n.includes("footer") || n.includes("items");
      });

    // de-dupe
    return [...new Set(names)];
  }

  async function refreshCartPageSections() {
    if (!location.pathname.includes("/cart")) return;

    const sectionNames = detectCartSectionNamesOnPage();
    if (!sectionNames.length) {
      // Fallback: at least nudge theme listeners
      document.dispatchEvent(new CustomEvent("cart:updated"));
      document.dispatchEvent(new CustomEvent("cart:refresh"));
      return;
    }

    // Fetch section HTML fragments from Shopify
    const res = await fetch(`/?sections=${encodeURIComponent(sectionNames.join(","))}`, {
      credentials: "same-origin",
      cache: "no-store",
      headers: { "X-Requested-With": "XMLHttpRequest" },
    });

    if (!res.ok) throw new Error(`sections refresh failed: ${res.status}`);
    const data = await res.json();

    sectionNames.forEach((name) => {
      const html = data[name];
      if (!html) return;

      const current = document.querySelector(`#shopify-section-${CSS.escape(name)}`);
      if (!current) return;

      // Parse returned HTML
      const doc = document.implementation.createHTMLDocument("");
      doc.documentElement.innerHTML = html;

      // Sometimes Shopify returns wrapper, sometimes only inner markup
      const freshWrapper = doc.querySelector(`#shopify-section-${CSS.escape(name)}`);

      if (freshWrapper) {
        current.replaceWith(freshWrapper);
      } else {
        // If no wrapper, just replace innerHTML
        current.innerHTML = html;
      }
    });

    // Let Sense re-bind its cart events after DOM swap
    document.dispatchEvent(new CustomEvent("shopify:section:load"));
    document.dispatchEvent(new CustomEvent("cart:updated"));
    document.dispatchEvent(new CustomEvent("cart:refresh"));
  }

  /* ---------------- show/hide panels ---------------- */
  const showPanels = (panels) => panels.forEach((p) => p.classList.remove("hidden"));
  const hidePanels = (panels) => panels.forEach((p) => p.classList.add("hidden"));

  /* ---------------- bind button once ---------------- */
  function bindButtons(panels, giftVariantId) {
    panels.forEach((panel) => {
      const btn =
        panel.querySelector(".pfp__btn") ||
        panel.querySelector(".free-gift-panel__btn") ||
        panel.querySelector("button");

      if (!btn) return;
      if (btn.dataset.bound === "1") return;

      btn.dataset.bound = "1";

      btn.addEventListener("click", async () => {
        btn.disabled = true;
        const original = btn.textContent || "Add for Free";

        try {
          const cart = await getCart();
          if (!cartHasGift(cart, giftVariantId)) {
            await addVariant(giftVariantId, 1, { _free_gift: "true" });
          }

          btn.textContent = "Added ✓";

          // THIS is the key for Sense cart page:
          await refreshCartPageSections();
        } catch (e) {
          console.error("Gift add failed:", e);
          btn.textContent = "Try again";
        } finally {
          setTimeout(() => {
            btn.textContent = original;
            btn.disabled = false;
          }, 1200);
        }
      });
    });
  }

  /* ---------------- main sync ---------------- */
  const sync = debounce(async () => {
    const panels = getPanels();
    if (!panels.length) return;

    const { giftVariantId, handles, autoRemove } = getConfig(panels[0]);
    if (!giftVariantId || !handles.length) return;

    // cache eligible ids
    const key = handles.slice().sort().join(",");
    if (!cachedEligibleIds || cachedHandlesKey !== key) {
      try {
        cachedEligibleIds = await getEligibleProductIds(handles);
        cachedHandlesKey = key;
      } catch (e) {
        console.warn("Eligible collection fetch failed:", e);
        hidePanels(panels);
        return;
      }
    }

    bindButtons(panels, giftVariantId);

    const cart = await getCart();
    const eligible = cartHasEligible(cart, cachedEligibleIds);
    const hasGift = cartHasGift(cart, giftVariantId);

    if (eligible && !hasGift) showPanels(panels);
    else hidePanels(panels);

    if (autoRemove && !eligible && hasGift) {
      try {
        await removeGiftIfPresent(cart, giftVariantId);
        await refreshCartPageSections();
      } catch (e) {
        console.warn("Gift auto-remove failed:", e);
      }
    }
  }, 120);

  /* ---------------- wiring ---------------- */
  document.addEventListener("DOMContentLoaded", sync);
  ["cart:updated", "cart:refresh", "shopify:section:load"].forEach((evt) =>
    document.addEventListener(evt, sync)
  );

  // Patch fetch so cart mutations always re-check visibility
  const _fetch = window.fetch;
  window.fetch = async (...args) => {
    const res = await _fetch(...args);
    try {
      const url = String(args[0] || "");
      if (
        url.includes("/cart/add") ||
        url.includes("/cart/change") ||
        url.includes("/cart/update") ||
        url.includes("/cart/clear")
      ) {
        setTimeout(() => {
          document.dispatchEvent(new CustomEvent("cart:updated"));
          document.dispatchEvent(new CustomEvent("cart:refresh"));
        }, 0);
      }
    } catch (e) {}
    return res;
  };
})();
